import type { FilterWithDisabledState } from '../../filters/components/FilterTags';
import { useRemoveFilter } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import type { UnpackedFilter } from '../../filters/hooks/useMultiFilterEditor';
import { FilterId } from '../../filters/types/filters';
import { unpackMultiFilter } from '../../filters/utils/unpackMultiFilter';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';

/**
 * Company pages take industries as their unit of analysis and Industry pages
 * take companies, so on those pages the sibling filter is already represented by
 * the primary entities and renders NO chip. Pages that list both as secondary
 * filters never reach this rule — they show a chip each. Every other unsupported
 * filter keeps the disabled-chip affordance: a Title filter set on Composition
 * stays visible (greyed) on Sentiment so the user can see it isn't applying.
 */
const INTERPRETATION_SIBLINGS: Partial<Record<FilterId, FilterId>> = {
  [FilterId.COMPANY]: FilterId.INDUSTRY,
  [FilterId.INDUSTRY]: FilterId.COMPANY,
};

export const useFilterTags = (): {
  filterTags: FilterWithDisabledState[];
  onRemoveFilter: (filterId: FilterId) => void;
} => {
  const effectiveFilters = useEffectiveFilters({
    includeDisabledFilters: true,
  });
  const onRemoveFilter = useRemoveFilter();
  const { hasFilter, hasSecondaryFilter } = useTabContentConfig();

  // Unset filters are not visible. Unpack multi filters into their instances.
  const visibleFilters: UnpackedFilter[] = effectiveFilters
    .flatMap((filter) => unpackMultiFilter(filter.id, filter))
    .filter((filter) => filter.isVisible && !filter.isPrimary);

  const visibleFilterIds = new Set(visibleFilters.map((filter) => filter.id));

  const filterTags: FilterWithDisabledState[] = visibleFilters
    .filter((filter) => {
      if (filter.parentMultiFilterId !== undefined) return true;
      const sibling = INTERPRETATION_SIBLINGS[filter.id];
      if (sibling === undefined) return true;
      // The page supports this reading — it applies, so always show it.
      if (hasSecondaryFilter(filter.id)) return true;
      // `hasFilter` (not `hasSecondaryFilter`) for the sibling: on company
      // analytics pages the company reading lands as the PRIMARY entities,
      // which represents the intent just as well as a secondary chip would.
      if (hasFilter(sibling)) return false;
      // The page supports NEITHER reading (Geography/Role Discovery list
      // neither COMPANY nor INDUSTRY). A lone reading still earns its disabled
      // chip, but with both set the symmetric check above let BOTH through —
      // two greyed chips saying the same thing. Keep the scoping axis only,
      // which is the Industry filter: that is what the screener resolver puts
      // `primary_selector.industry` into (`PRIMARY_INDUSTRY_FILTER_ID`).
      return !visibleFilterIds.has(sibling) || filter.id === FilterId.INDUSTRY;
    })
    .map((filter) => {
      const isMultiInstance = filter.parentMultiFilterId !== undefined;
      return {
        filter,
        disabled: isMultiInstance ? false : !hasSecondaryFilter(filter.id),
      };
    });

  return { filterTags, onRemoveFilter };
};
